/**
 * schedulingState.ts — Persisted scheduling state for the idle → scheduled transition.
 *
 * M2-UX-P0-011: Wire idle → scheduled lifecycle transition.
 *
 * This module owns the scheduling data between app launches.
 * The selected retreat + scheduled time are written to localStorage
 * on confirm and read at boot to determine if deriveScreen() should
 * route to the home-scheduled state.
 *
 * Invariants:
 *   - Wall-clock is source of truth (system-graph invariant #1)
 *   - Past scheduled times auto-clear to idle (no zombie schedules)
 *   - localStorage unavailable → graceful fallback (no crash)
 */

import type { EditableRetreatSchedule } from "@retreat/core";
import { EditableRetreatScheduleSchema } from "@retreat/core";
import { STORAGE_KEYS, VALID_WAKE_OFFSETS } from "../constants";
import type { WakeOffset } from "../constants";

// ── Storage keys ────────────────────────────────────────────────
const STORAGE_KEY_RETREAT = STORAGE_KEYS.SCHEDULED_RETREAT;
const STORAGE_KEY_DATETIME = STORAGE_KEYS.SCHEDULED_DATETIME;
const STORAGE_KEY_LAST_USED = STORAGE_KEYS.LAST_USED_SCHEDULE;

// ── Types ───────────────────────────────────────────────────────

/**
 * ScheduledRetreat — the minimal data persisted for a booked retreat.
 * Stores the full schedule (needed by the engine at start) plus the
 * wall-clock start time.
 */
export type ScheduledRetreat = {
  schedule: EditableRetreatSchedule;
  scheduledAt: number; // Unix timestamp (ms) — when the retreat should start
};

// ── Read ────────────────────────────────────────────────────────

/**
 * Load the scheduled retreat from localStorage.
 * Returns null if no schedule exists, if the scheduled time is in the past,
 * or if localStorage is unavailable.
 */
export function loadScheduledRetreat(): ScheduledRetreat | null {
  try {
    const retreatJson = localStorage.getItem(STORAGE_KEY_RETREAT);
    const datetimeStr = localStorage.getItem(STORAGE_KEY_DATETIME);

    if (!retreatJson || !datetimeStr) return null;

    const scheduledAt = Number(datetimeStr);

    if (!scheduledAt || isNaN(scheduledAt)) {
      clearScheduledRetreat();
      return null;
    }

    // Past scheduled times auto-clear — no zombie schedules
    if (scheduledAt < Date.now()) {
      clearScheduledRetreat();
      return null;
    }

    const parsed = EditableRetreatScheduleSchema.safeParse(
      JSON.parse(retreatJson),
    );
    if (!parsed.success) {
      clearScheduledRetreat();
      return null;
    }

    return { schedule: parsed.data, scheduledAt };
  } catch {
    // localStorage unavailable or corrupt data
    return null;
  }
}

// ── Write ───────────────────────────────────────────────────────

/**
 * Persist a scheduled retreat to localStorage.
 */
export function saveScheduledRetreat(
  schedule: EditableRetreatSchedule,
  scheduledAt: number,
): void {
  try {
    localStorage.setItem(STORAGE_KEY_RETREAT, JSON.stringify(schedule));
    localStorage.setItem(STORAGE_KEY_DATETIME, String(scheduledAt));
  } catch {
    // localStorage unavailable — silent fail, retreat won't persist across refresh
  }
}

// ── Last-used schedule ──────────────────────────────────────────

/**
 * Persist the most recently selected schedule so the home screen
 * can show retreat name/duration on next launch (M2-UX-P0-001).
 */
export function saveLastUsedSchedule(
  schedule: EditableRetreatSchedule,
): void {
  try {
    localStorage.setItem(STORAGE_KEY_LAST_USED, JSON.stringify(schedule));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Load the most recently used schedule, or null if none saved yet.
 */
export function loadLastUsedSchedule(): EditableRetreatSchedule | null {
  try {
    const json = localStorage.getItem(STORAGE_KEY_LAST_USED);
    if (!json) return null;
    const parsed = EditableRetreatScheduleSchema.safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// ── Wake offset ─────────────────────────────────────────────────

export function saveWakeOffset(offset: WakeOffset): void {
  try {
    localStorage.setItem(STORAGE_KEYS.WAKE_OFFSET, offset);
  } catch { /* localStorage unavailable */ }
}

export function loadWakeOffset(): WakeOffset {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.WAKE_OFFSET);
    if (raw && VALID_WAKE_OFFSETS.includes(raw as WakeOffset)) return raw as WakeOffset;
  } catch { /* ignore */ }
  return '30min';
}

// ── Clear ───────────────────────────────────────────────────────

/**
 * Clear the scheduled retreat. Called on cancel or after retreat starts.
 */
export function clearScheduledRetreat(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_RETREAT);
    localStorage.removeItem(STORAGE_KEY_DATETIME);
  } catch {
    // localStorage unavailable
  }
}
